/*
 * =====================================================================
 * 작 업 명  :
 * 파 일 명  : drive.js
 * 변경이력
 * 2023-01-05 / 이성준 / 최초작성
 * =====================================================================
 */
import {$session} from "dot-framework"
import DTMSCanvas from "@/util/DTMSCanvas"
import {DotPadInfo} from "@/util/dotpad/DotPadInfo.js"
import driveApi from "@/api/apps/driveApi"

// card형 목록의 DTMS 썸네일 생성을 위한 캔버스 배열
const canvas = [];
const canvasWidth = 99;
const canvasHeight = 99;
/**
 * @type {Readonly<{CUT: symbol, COPY: symbol}>}
 */
const CLIPBOARD_MODE = Object.freeze({
    CUT: Symbol("CUT"),
    COPY: Symbol("COPY"),
});
/**
 * @type {Readonly<{CARD: symbol, LIST: symbol}>}
 */
const VIEW_MODE = Object.freeze({
    CARD: Symbol("CARD"),
    LIST: Symbol("LIST"),
});

const dtmsDataFormatter = (data) => {
    return (data && data.items)
        ? data.items.map(item => ({
            ...item,
            DTMS_JSON: item.DTMS_JSON
                ? {
                    ...item.DTMS_JSON,
                    items: item.DTMS_JSON.items?.map(subItem => ({
                        ...subItem,
                        dotPadInfo: new DotPadInfo(subItem.DEVICE_KIND)
                    }))
                }
                : item.DTMS_JSON // DTMS_JSON이 없으면 원래 값 유지
        }))
        : null
}

const state = () => ({
    clipboard: [], // 선택한 행 복사(클립보드)
    clipboardMode: null,
    currentGroup: "",
    driverKind: "P", // Private Drive
    items: [],
    redoStack: [],
    selectedItems: [],
    undoStack: [],
    viewMode: VIEW_MODE.CARD,
    currentPage: 1,
    totalPage: 1,
});

// getters
const getters = {
    clipboard: (state) => {
        return state.clipboard;
    },
    clipboardMode: (state) => {
        return state.clipboardMode;
    },
    driverKind: (state) => {
        return state.driverKind;
    },
    filteredItems: (state) => (textFilter) => {
        //FIXME: metacharacter(?:^|\\W)
        textFilter = textFilter.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(textFilter, 'i');

        return state.items.filter(item => regex.test(item.FILE_NAME));
    },
    getCurrentGroupNo: (state) => {
        return state.currentGroup;
    },
    isPrivate: (state) => {
        return state.driverKind === "P";
    },
    isPublic: (state) => {
        return state.driverKind === "D";
    },
    items: (state) => {
        return state.items;
    },
    selectedItems: (state) => {
        return state.selectedItems;
    },
    viewMode: (state) => {
        return state.viewMode;
    },
    currentPage: (state) => {
        return state.currentPage;
    },
    totalPage: (state) => {
        return state.totalPage;
    }
};

const findIndexOfSelectedItem = (state, item) => {
    return state.selectedItems.findIndex(x => x.FILE_KEY === item.FILE_KEY);
};

// mutations
const mutations = {
    clearClipboard(state) {
        state.clipboard = [];
    },
    clearSelectedItems(state) {
        state.selectedItems = []
    },
    pushToClipboard(state, clipboard) {
        state.clipboard.push(...clipboard)
    },
    pushToSelectedItems(state, data) {
        const items = Array.isArray(data) ? data : [data];

        for (const item of items) {
            const indexToUpdate = findIndexOfSelectedItem(state, item);

            if (indexToUpdate !== -1) {
                state.selectedItems[indexToUpdate] = item;
            } else {
                state.selectedItems.push(item);
            }
        }
    },
    removeSelectedItem(state, item) {
        const indexToRemove = findIndexOfSelectedItem(state, item);

        if (indexToRemove !== -1) {
            state.selectedItems.splice(indexToRemove, 1);
        }
    },
    redo(state) {
        if (state.redoStack.length === 0) return;
        const command = state.redoStack.pop();
        command.redo();
        state.undoStack.push(command);
    },
    setClipboardMode(state, mode) {
        state.clipboardMode = CLIPBOARD_MODE[mode];
    },
    setCurrentGroupNo(state, groupNo) {
        state.currentGroup = groupNo;
    },
    setDriveKindStatus(state, status) {
        state.driverKind = status;
    },
    setItems(state, items) {
        state.items = items;
    },
    setViewMode(state, status) {
        state.viewMode = VIEW_MODE[status];
    },
    undo(state) {
        if (state.undoStack.length === 0) return;
        const command = state.undoStack.pop();
        command.undo();
        state.redoStack.push(command);
    },
    pushUndoStack(state, command) {
        state.undoStack.push(command);
        // 새 커맨드를 실행할 때마다 redo 스택 초기화
        state.redoStack = [];
    },
    setCurrentPage(state, command) {
        state.currentPage = command;
    },
    setTotalPage(state, command) {
        state.totalPage = command;
    }
};

// actions
const actions = {
    /**
     * [Dot Cloud]
     * 메뉴에서 Card형 목록을 볼 때, Card 안에 DTMS 썸네일 이미지를 생성
     * @param context
     */
    createDTMSCanvasOfCardTypeItems(context) {
        for (let i = 0; i < context.state.items.length; i++){
            const obj = context.state.items[i]
            const DEVICE_KIND = context.state.items[i].DEVICE_KIND + ""
            if (obj.FILE_TYPE === 'FILE') {
                const DTMSJson = obj.DTMS_JSON

                if (DTMSJson && Array.isArray(DTMSJson.items) && DTMSJson.items.length > 0) {
                    const dotPadInfo = new DotPadInfo(DEVICE_KIND)
                    DTMSJson.items[0].dotPadInfo = dotPadInfo
                    const pixelArr = DTMSCanvas.getPixelArray(DTMSJson.items[0])
                    const params = {
                        pixelArr: pixelArr,
                        canvasId: `canvas-${obj.FILE_KEY}-0`,
                        canvasWidth,
                        canvasHeight,
                        dotPadInfo
                    }

                    canvas[i] !== undefined && canvas[i].dispose()
                    const staticCanvas = DTMSCanvas.createStaticCanvas(params.canvasId, params.canvasWidth, params.canvasHeight)
                    staticCanvas.add(DTMSCanvas.drawTactileDataOnTheCanvasForChangeTest(params))
                    canvas[i] = staticCanvas
                }
            }
        }

        canvas.splice(context.state.items.length)
    },
    executeCommand(context, command) {
        command.execute();
        context.commit("pushUndoStack", command)
    },
    /**
     * @param context
     * @param payload
     * @param {string} payload.parentGroupNo    부모 촉각이미지 그룹번호
     * @param {string|number} payload.pageNo    페이지 번호
     * @param {string|number} payload.pageSize  페이지 사이즈
     * @param {string} payload.appType          A: All, C: Canvas, T: Text Book
     * @param {string} payload.driveKind        D: Drive, P: Private
     */
    async fetchDtmsGroupList(context, payload) {
        let dtmsResponse = await context.dispatch('getDtmsGroupList', payload)
        if (dtmsResponse.status === 401) {
            setTimeout(async () => await context.dispatch("reloadItems", payload), 1000)
        }

        context.commit("setCurrentPage", dtmsResponse.pageNo)
        context.commit("setCurrentGroupNo", dtmsResponse.parentGroupNo)
        context.commit("setItems", dtmsResponse.items)
        context.commit("setTotalPage", dtmsResponse.totalPage)
    },
    redo(context) {
        context.commit("redo");
    },
    async getDtmsGroupList(context, payload, rootState, rootGetters) {
        const parentGroupNo = payload?.parentGroupNo || (new URLSearchParams(window.location.search).get('parentGroupNo') || "ROOT")
        const pageNo = payload?.pageNo || 1
        const pageSize = payload?.pageSize || 18
        const appType = payload?.appType || 'A'
        const driverKind = payload?.driveKind || context.state.driverKind
        const params = {
            SITE_NO: $session.getSiteNo(),
            PAGE_NO: pageNo,
            PAGE_SIZE: pageSize,
            COMP_NO: $session.getCompNo() || rootGetters.COMP_NO,
            PARENT_GROUP_NO: parentGroupNo,
            DRIVER_KIND: driverKind,
            USER_NO: $session.getUserNo() !== "GUEST" ? $session.getUserNo() : "",
            APP_TYPE: appType,
        }

        const response = await driveApi.getDtmsGroupList(params)

        let totalCount = 0
        let totalPage = 0

        if (response.data) {
            totalCount = response.data.TOTAL_COUNT || 0
            totalPage = Math.ceil(totalCount/pageSize)
        }

        return {
            status: response.status,
            items: dtmsDataFormatter(response.data),
            pageNo: pageNo,
            parentGroupNo: parentGroupNo,
            totalPage: totalPage
        }

    },
    async reloadItems(context, payload) {
        await context.dispatch("fetchDtmsGroupList", payload)
    },
    undo(context) {
        context.commit("undo")
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
